import React, { useState, useEffect } from 'react';
import { Volume2, Play, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import VoiceCloningPanel from './VoiceCloningPanel';

export default function VoiceSettings({ presenceId }) {
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    loadData();
  }, [presenceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load voice config
      const configRes = await api.get(`/presence/${presenceId}/voice-config`);
      setVoiceConfig(configRes.data);
      
      // Load available voices
      const voicesRes = await api.get('/voices/openai-options');
      setVoices(voicesRes.data.voices);
      
      // Load subscription
      const subRes = await api.get('/subscription/current');
      setSubscription(subRes.data);
      
    } catch (err) {
      console.error('Error loading voice settings:', err);
      toast.error('Ses ayarları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceChange = (voiceId) => {
    setVoiceConfig({
      ...voiceConfig,
      openai_voice: voiceId
    });
  };

  const handleSpeedChange = (speed) => {
    setVoiceConfig({
      ...voiceConfig,
      speed: parseFloat(speed)
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await api.put(`/presence/${presenceId}/voice-config`, voiceConfig);
      
      toast.success('Ses ayarları kaydedildi!');
    } catch (err) {
      console.error('Error saving voice config:', err);
      toast.error(err.response?.data?.detail || 'Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (voiceId) => {
    try {
      setPlaying(voiceId);
      
      const res = await api.post('/voices/preview', {
        voice_id: voiceId,
        text: 'Merhaba, ben sizin dijital varlığınızım. Bu benim sesim.'
      });
      
      // Decode and play audio
      const audioData = atob(res.data.audio);
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }
      
      const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlaying(null);
        URL.revokeObjectURL(audioUrl);
      };
      
      await audio.play();
      
    } catch (err) {
      console.error('Preview error:', err);
      toast.error('Önizleme çalınamadı');
      setPlaying(null);
    }
  };

  const canUseElevenLabs = subscription?.tier === 'premium' || subscription?.tier === 'pro';

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-slate-400">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          🎤 Ses ve Ses Tonu
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Dijital varlığınızın sesini seçin ve özelleştirin
        </p>
      </div>

      {/* OpenAI Voices */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Ses Seçimi (OpenAI)
          </h3>
          <div className="text-xs px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            Tüm Planlarda Mevcut
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {voices.map((voice) => (
            <button
              key={voice.id}
              onClick={() => handleVoiceChange(voice.id)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                voiceConfig?.openai_voice === voice.id
                  ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Volume2 size={20} className="text-cyan-600 dark:text-cyan-400" />
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {voice.name}
                  </span>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(voice.id);
                  }}
                  disabled={playing === voice.id}
                  className="p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  {playing === voice.id ? (
                    <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Play size={16} className="text-slate-600 dark:text-slate-400" />
                  )}
                </button>
              </div>
              
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {voice.description}
              </p>
              
              <div className="mt-2 flex gap-2">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                  {voice.gender}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Speed Control */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Konuşma Hızı
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Hız: {voiceConfig?.speed || 1.0}x
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-500">
              {voiceConfig?.speed < 1 ? 'Yavaş' : voiceConfig?.speed > 1 ? 'Hızlı' : 'Normal'}
            </span>
          </div>
          
          <input
            type="range"
            min="0.25"
            max="4.0"
            step="0.25"
            value={voiceConfig?.speed || 1.0}
            onChange={(e) => handleSpeedChange(e.target.value)}
            className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-600"
          />
          
          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-500">
            <span>0.25x</span>
            <span>1.0x</span>
            <span>4.0x</span>
          </div>
        </div>
      </div>

      {/* ElevenLabs Voice Cloning */}
      {canUseElevenLabs ? (
        <VoiceCloningPanel
          presenceId={presenceId}
          onVoiceCloned={async (voiceData) => {
            // Auto-select the cloned voice
            setVoiceConfig({
              ...voiceConfig,
              provider: 'elevenlabs',
              elevenlabs_voice_id: voiceData.elevenlabs_voice_id
            });
            
            // Auto-save
            try {
              await api.put(`/presence/${presenceId}/voice-config`, {
                ...voiceConfig,
                provider: 'elevenlabs',
                elevenlabs_voice_id: voiceData.elevenlabs_voice_id
              });
              toast.success('Voice automatically selected!');
            } catch (err) {
              console.error('Auto-select error:', err);
            }
          }}
        />
      ) : (
        <div className="bg-gradient-to-br from-purple-50 to-cyan-50 dark:from-purple-900/20 dark:to-cyan-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <Crown className="text-white" size={24} />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Ses Klonlama
                </h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500 text-white">
                  PREMIUM
                </span>
              </div>
              
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Kendi sesinizi klonlayın ve dijital varlığınız sizin sesinizle konuşsun. 
                ElevenLabs AI teknolojisi ile gerçekçi ses klonlama.
              </p>
              
              <button
                onClick={() => toast.info('Premium plana yükseltin')}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-medium transition-all"
              >
                Premium'a Yükselt ($29/ay)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <button
          onClick={loadData}
          className="px-6 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          İptal
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white font-medium transition-all disabled:opacity-50"
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  );
}
